import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Chart, registerables } from 'chart.js';
import 'chartjs-adapter-date-fns';
import { fetchConsumptionBasedGHGData, ConsumptionBasedGHGResult } from '../../services/consumptionBasedGHGService';

Chart.register(...registerables);

type ChartType = 'stacked-area' | 'line' | 'pie';

interface ConsumptionBasedGHGInventoryProps {
  location: string;
  chartType: ChartType;
  setChartType: (chartType: ChartType) => void;
}

export const ConsumptionBasedGHGInventory: React.FC<ConsumptionBasedGHGInventoryProps> = ({ location, chartType, setChartType }) => {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ghgData, setGhgData] = useState<ConsumptionBasedGHGResult | null>(null);

  // Load data from Supabase
  useEffect(() => {
    const loadData = async () => {
      if (!location) return;
      
      try {
        setLoading(true);
        setError(null);
        
        const result = await fetchConsumptionBasedGHGData(location);
        setGhgData(result);
      } catch (err) {
        console.error('Error loading consumption-based GHG data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load consumption-based GHG data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [location]);

  const createChart = useCallback(() => {
    if (!chartRef.current || !ghgData) return;

    const ctx = chartRef.current.getContext('2d');
    if (!ctx) return;

    // Destroy existing chart
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    let chartConfig: any;

    if (chartType === 'pie') {
      // Pie chart for latest year data only
      const latestData = ghgData.chartData.datasets.map(dataset => {
        const latestValue = dataset.data[dataset.data.length - 1];
        return { label: dataset.label, value: latestValue, color: dataset.backgroundColor };
      });
      
      chartConfig = {
        type: 'pie',
        data: {
          labels: latestData.map(d => d.label),
          datasets: [{
            data: latestData.map(d => d.value),
            backgroundColor: latestData.map(d => d.color),
            borderColor: '#ffffff',
            borderWidth: 2
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          layout: {
            padding: {
              top: 10,
              bottom: 10,
              left: 10,
              right: 10
            }
          },
          plugins: {
            legend: {
              position: 'bottom',
              labels: {
                usePointStyle: true,
                padding: 15,
                font: {
                  size: 13
                }
              }
            },
            tooltip: {
              callbacks: {
                label: function(context: any) {
                  const label = context.label || '';
                  const value = context.parsed;
                  const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
                  const percentage = ((value / total) * 100).toFixed(1);
                  return `${label}: ${value.toLocaleString()} MT CO₂e (${percentage}%)`;
                }
              }
            }
          }
        }
      };
    } else {
      // Line or stacked area chart for time series
      const isStacked = chartType === 'stacked-area';
      
      chartConfig = {
        type: 'line',
        data: ghgData.chartData,
        options: {
          responsive: true,
          maintainAspectRatio: false,
          layout: {
            padding: {
              top: 10,
              bottom: 10,
              left: 10,
              right: 10
            }
          },
          plugins: {
            legend: {
              position: 'bottom',
              labels: {
                usePointStyle: true,
                padding: 15,
                font: {
                  size: 13
                }
              }
            },
            tooltip: {
              callbacks: {
                label: function(context: any) {
                  const label = context.dataset.label || '';
                  const value = context.parsed.y;
                  return `${label}: ${value.toLocaleString()} MT CO₂e`;
                }
              }
            }
          },
          scales: {
            x: {
              title: {
                display: true,
                text: 'Year',
                font: {
                  size: 13,
                  weight: ('bold' as const)
                },
                padding: { top: 10, bottom: 5 }
              },
              ticks: {
                font: {
                  size: 12
                },
                maxRotation: 45,
                minRotation: 45
              },
              grid: {
                display: false
              }
            },
            y: {
              title: {
                display: true,
                text: 'GHG Emissions (MT CO₂e)',
                font: {
                  size: 13,
                  weight: ('bold' as const)
                },
                padding: { top: 5, bottom: 10 }
              },
              beginAtZero: true,
              grid: {
                color: 'rgba(0, 0, 0, 0.05)'
              },
              ticks: {
                font: {
                  size: 12
                },
                callback: (value: any) => Number(value).toLocaleString()
              }
            }
          },
          interaction: {
            intersect: false,
            mode: 'index'
          }
        }
      };

      // Update datasets for stacked area
      if (isStacked) {
        chartConfig.data.datasets = chartConfig.data.datasets.map((dataset: any) => ({
          ...dataset,
          fill: true,
          backgroundColor: dataset.backgroundColor.replace('0.1', '0.8'),
          pointRadius: 0, // Remove dots from charts
          stack: 'stack1' // Add stack property for proper stacking
        }));
        
        // Add stacking configuration to scales
        chartConfig.options.scales = {
          ...chartConfig.options.scales,
          x: {
            ...chartConfig.options.scales.x,
            stacked: true
          },
          y: {
            ...chartConfig.options.scales.y,
            stacked: true
          }
        };
      } else {
        // For line charts, ensure no stacking and proper line styling
        chartConfig.data.datasets = chartConfig.data.datasets.map((dataset: any) => ({
          ...dataset,
          fill: false,
          tension: 0.2,
          pointRadius: 0, // Remove dots from charts
          pointHoverRadius: 0
        }));
        
        // Ensure scales are not stacked for line charts
        chartConfig.options.scales = {
          ...chartConfig.options.scales,
          x: {
            ...chartConfig.options.scales.x,
            stacked: false
          },
          y: {
            ...chartConfig.options.scales.y,
            stacked: false
          }
        };
      }
    }

    chartInstance.current = new Chart(ctx, chartConfig);
  }, [chartType, ghgData]);

  useEffect(() => {
    if (ghgData) {
      createChart();
    }

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [chartType, location, createChart, ghgData]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <div>Loading consumption-based GHG data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', color: '#ef4444' }}>
        <div>Error: {error}</div>
      </div>
    );
  }

  if (!ghgData) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <div>No consumption-based GHG data available for {location}</div>
      </div>
    );
  }

  return (
    <div style={{ 
      width: '100%', 
      height: '100%',
      minHeight: '400px',
      position: 'relative',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <canvas ref={chartRef} />
    </div>
  );
};